import React, { useState, useRef, useEffect, useContext } from 'react';
import { DynamicIcon } from './icons.jsx';
import { ThemeContext } from '../context/ThemeContext.jsx';
import { useNavigate } from 'react-router-dom';
import { useData } from '../hooks/useData.js';
import { Snackbar, Alert, Tooltip, Zoom } from '@mui/material';
import { Avatar } from './Common.jsx';
import { apiPost, apiFetch } from '../api';

const UserProfileMenu = ({ user, onLogout, token, onProfileClick, onRestartTour }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const { theme, setTheme } = useContext(ThemeContext);
    const navigate = useNavigate();

    const { data: demos } = useData('/tenant-onboarding/me/demos', token);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleThemeChange = async (newMode) => {
        setTheme(prev => ({ ...prev, mode: newMode }));
        if (token) {
            try {
                await apiFetch('/users/me/theme', token, {
                    method: 'PUT',
                    body: JSON.stringify({ mode: newMode })
                });
            } catch (err) {
                console.error('Failed to persist theme mode:', err);
            }
        }
    };

    const ROLE_MAP = {
        'r1': 'Super Admin',
        'r2': 'Platform Admin',
        'r3': 'Partner Admin',
        'r4': 'Tenant Admin',
        'r7': 'Standard User'
    };

    const roleLabel = ROLE_MAP[user?.roleId] || 'User';

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                id="user-profile-menu"
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center p-0.5 rounded-full transition-all duration-300 border-2 ${isOpen ? 'border-primary-500 ring-4 ring-primary-500/10' : 'border-transparent hover:border-gray-200'}`}
                title="Account"
            >
                <Avatar 
                    src={user?.profile_pic ? (user.profile_pic.startsWith('data:') ? user.profile_pic : `data:image/png;base64,${user.profile_pic}`) : null}
                    name={user?.name || 'User'}
                    size={36}
                />
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-3 w-[310px] bg-white dark:bg-gray-900 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.4)] border border-gray-100 dark:border-gray-800 overflow-hidden z-[100] animate-in fade-in zoom-in-95 duration-200 origin-top-right">
                    {/* User Profile Summary */}
                    <div className="p-5 pb-4 bg-gray-50/50 dark:bg-gray-800/30">
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                <Avatar 
                                    src={user?.profile_pic ? (user.profile_pic.startsWith('data:') ? user.profile_pic : `data:image/png;base64,${user.profile_pic}`) : null}
                                    name={user?.name || 'User'}
                                    size={52}
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h4 className="text-[16px] font-extrabold text-gray-900 dark:text-white leading-tight truncate">
                                    {user?.name || 'User'}
                                </h4>
                                <p className="text-[12px] text-gray-500 dark:text-gray-400 truncate font-medium mt-0.5">
                                    {user?.email}
                                </p>
                            </div>
                        </div>
                        <div className="mt-4">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-bold tracking-wider uppercase bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 border border-primary-100 dark:border-primary-800">
                                {roleLabel}
                            </span>
                        </div>
                    </div>

                    <div className="h-[1px] bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent mx-4"></div>

                    {/* Navigation Actions */}
                    <div className="p-2 pt-3">
                        <button
                            onClick={() => {
                                setIsOpen(false);
                                if (onProfileClick) onProfileClick();
                                else navigate('/profile');
                            }}
                            className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 group"
                        >
                            <div className="flex items-center gap-3">
                                <div className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 group-hover:bg-primary-50 group-hover:text-primary-600 dark:group-hover:bg-primary-900/30 dark:group-hover:text-primary-400 transition-colors">
                                    <DynamicIcon name="User" className="w-4 h-4" />
                                </div>
                                <span className="text-[14px] font-bold text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">My Profile</span>
                            </div>
                            <DynamicIcon name="ChevronRight" className="w-4 h-4 text-gray-300 group-hover:text-gray-400 group-hover:translate-x-1 transition-all" />
                        </button>
                        
                        {onRestartTour && (
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    onRestartTour();
                                }}
                                className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 group"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="p-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-500 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/40 transition-colors">
                                        <DynamicIcon name="Sparkles" className="w-4 h-4" />
                                    </div>
                                    <span className="text-[14px] font-bold text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">Quick Tour</span>
                                </div>
                                <DynamicIcon name="ChevronRight" className="w-4 h-4 text-gray-300 group-hover:text-gray-400 group-hover:translate-x-1 transition-all" />
                            </button>
                        )}
                    </div>

                    {/* Appearance / Theme Segmented Control */}
                    <div className="px-5 py-4 pb-5">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-gray-400 dark:text-gray-500">Appearance</span>
                        </div>
                        <div className="grid grid-cols-3 gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl">
                            {[
                                { mode: 'light', icon: 'Sun', label: 'Light' },
                                { mode: 'dark',  icon: 'Moon', label: 'Dark' },
                                { mode: 'system', icon: 'Monitor', label: 'System' },
                            ].map(({ mode, icon, label }) => {
                                const isActive = theme.mode === mode;
                                return (
                                    <button
                                        key={mode}
                                        onClick={() => handleThemeChange(mode)}
                                        className={`flex flex-col items-center gap-1 py-1.5 rounded-lg transition-all duration-200 ${
                                            isActive
                                                ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm ring-1 ring-black/5'
                                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                                        }`}
                                    >
                                        <DynamicIcon name={icon} className={`w-3.5 h-3.5 ${isActive ? 'scale-110' : ''}`} />
                                        <span className="text-[10px] font-bold uppercase tracking-wider">{label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="h-[1px] bg-gray-100 dark:bg-gray-800 mx-2"></div>

                    {/* Sign Out */}
                    <div className="p-2">
                        <button
                            onClick={() => {
                                setIsOpen(false);
                                onLogout();
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-all duration-200 group"
                        >
                            <div className="p-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 group-hover:bg-red-100 dark:group-hover:bg-red-900/30 transition-colors">
                                <DynamicIcon name="LogOut" className="w-4 h-4" />
                            </div>
                            <span className="text-[14px] font-bold">Sign out</span>
                        </button>
                    </div>
                </div>
            )}

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default UserProfileMenu;
